import React from 'react'
import { RefreshCw, Radio, Moon } from 'lucide-react'
import { useDesk } from '../deskContext'
import { formatPrice, formatSignedPercent, formatCompact, formatTime } from '../utils/format'
import { toneColor } from '../utils/tone'
import { marketSession } from '../utils/session'

export default function MarketHeader({ quote, asOf, source, onRefresh, loading, priceTarget, committee }) {
  const desk = useDesk()
  const up = (quote?.dayChange ?? 0) >= 0
  const color = toneColor(up ? 'bull' : 'bear')
  const session = marketSession()
  const ext = quote?.extended
  const extUp = (ext?.change ?? 0) >= 0
  const showExtended = ext && session.key !== 'regular'

  const counts = (committee ?? []).reduce(
    (a, m) => {
      if (m.action === 'BUY_MORE') a.buy += 1
      else if (m.action === 'HOLD' || m.action === 'TRIM') a.hold += 1
      else a.sell += 1
      return a
    },
    { buy: 0, hold: 0, sell: 0 }
  )
  const hasCommittee = (committee?.length ?? 0) > 0

  const analyst = quote?.analyst
  const analystCounts = analyst
    ? {
        buy: (analyst.strongBuy ?? 0) + (analyst.buy ?? 0),
        hold: analyst.hold ?? 0,
        sell: (analyst.sell ?? 0) + (analyst.strongSell ?? 0),
      }
    : null

  return (
    <div className="onds-card p-4 md:p-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 md:gap-5">
      <div className="flex items-center gap-4 md:gap-5">
        <div
          className="w-12 h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center text-lg md:text-xl font-bold onds-pulse-ring shrink-0"
          style={{ backgroundColor: 'var(--onds-accent)', color: '#0a0e17' }}
        >
          {desk.ticker}
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl md:text-2xl font-bold tracking-tight">{desk.companyName}</h1>
            <SessionBadge session={session} />
          </div>
          <p className="text-xs text-[var(--onds-text-dim)] mt-0.5">{desk.sector}</p>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mt-1">
            <span className="text-2xl md:text-3xl font-bold tabular-nums">{formatPrice(quote?.price)}</span>
            <span className="text-base md:text-lg font-semibold tabular-nums" style={{ color }}>
              {up ? '▲' : '▼'} {formatPrice(Math.abs(quote?.dayChange ?? 0))} ({formatSignedPercent(quote?.dayChangeRatio)})
            </span>
          </div>
          {showExtended && (
            <div className="flex items-center gap-1.5 mt-1 text-xs md:text-sm">
              <Moon className="h-3.5 w-3.5" style={{ color: 'var(--onds-text-dim)' }} />
              <span className="text-[var(--onds-text-dim)]">{session.label}</span>
              <span className="font-semibold tabular-nums">{formatPrice(ext.price)}</span>
              <span className="font-semibold tabular-nums" style={{ color: toneColor(extUp ? 'bull' : 'bear') }}>
                {extUp ? '+' : ''}{formatSignedPercent(ext.changeRatio)}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-start justify-between lg:justify-end gap-4 md:gap-6">
        <div className="flex flex-wrap items-start gap-x-5 gap-y-2 text-xs md:text-sm max-w-[560px]">
          {quote?.marketCap > 0 && <Stat label="Mkt Cap" value={formatCompact(quote.marketCap)} />}
          <Stat label="Vol" value={formatCompact(quote?.volume)} />
          <Stat label="Avg Vol" value={formatCompact(quote?.avgVolume)} />
          <Stat label="6M High" value={formatPrice(quote?.sixMonthHigh || quote?.week52High)} />
          <Stat label="6M Low" value={formatPrice(quote?.sixMonthLow || quote?.week52Low)} />
          {analyst?.targetMean > 0 && (
            <Stat label={`Street Tgt${analyst.numberOfAnalysts ? ` · ${analyst.numberOfAnalysts}` : ''}`} value={formatPrice(analyst.targetMean)} />
          )}
          {analystCounts && (
            <Stat label="Street B/H/S" value={`${analystCounts.buy} / ${analystCounts.hold} / ${analystCounts.sell}`} />
          )}
          {priceTarget != null && <Stat label="Committee Tgt" value={formatPrice(priceTarget)} color="var(--onds-accent)" />}
          {hasCommittee && (
            <Stat label="Committee B/H/S" value={`${counts.buy} / ${counts.hold} / ${counts.sell}`} color="var(--onds-accent)" />
          )}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <button
            onClick={onRefresh}
            disabled={loading}
            className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-semibold transition disabled:opacity-50 whitespace-nowrap"
            style={{ backgroundColor: 'var(--onds-accent)', color: '#0a0e17' }}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Analyzing' : 'Re-run'}
          </button>
          <span className="flex items-center gap-1.5 text-[10px] md:text-[11px] text-[var(--onds-text-dim)] text-right">
            <Radio className="h-3 w-3 shrink-0" style={{ color: sourceMeta(source).color }} />
            {sourceMeta(source).label} · {formatTime(asOf)}
          </span>
        </div>
      </div>
    </div>
  )
}

function sourceMeta(source) {
  if (source === 'LIVE_QUOTE') return { label: 'Live quote · sim analysis', color: 'var(--onds-bull)' }
  if (source === 'LIVE') return { label: 'Live', color: 'var(--onds-bull)' }
  return { label: 'Simulated', color: 'var(--onds-caution)' }
}

function SessionBadge({ session }) {
  const color = toneColor(session.tone)
  return (
    <span className="onds-chip" style={{ color, borderColor: `${color}55`, backgroundColor: `${color}14` }}>
      <span className={`w-1.5 h-1.5 rounded-full ${session.live ? 'onds-pulse-ring' : ''}`} style={{ backgroundColor: color }} />
      {session.label}
    </span>
  )
}

function Stat({ label, value, color }) {
  return (
    <div className="flex flex-col leading-tight">
      <span className="text-[10px] uppercase tracking-wider text-[var(--onds-text-dim)] whitespace-nowrap">{label}</span>
      <span className="font-semibold tabular-nums whitespace-nowrap" style={color ? { color } : undefined}>{value}</span>
    </div>
  )
}
